import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import api from "@/lib/api";
import {
  BarChart3, TrendingUp, Wallet, MapPinned, MessagesSquare, Users, Mail, CalendarClock,
  Package, Store,
} from "lucide-react";

const PERIODS = [
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "all", label: "Tout" },
];

const CANTON_NAMES = {
  AG: "Argovie", AI: "Appenzell RI", AR: "Appenzell RE", BE: "Berne", BL: "Bâle-C.", BS: "Bâle-V.",
  FR: "Fribourg", GE: "Genève", GL: "Glaris", GR: "Grisons", JU: "Jura", LU: "Lucerne", NE: "Neuchâtel",
  NW: "Nidwald", OW: "Obwald", SG: "St-Gall", SH: "Schaffhouse", SO: "Soleure", SZ: "Schwytz",
  TG: "Thurgovie", TI: "Tessin", UR: "Uri", VD: "Vaud", VS: "Valais", ZG: "Zoug", ZH: "Zurich",
};

const chf = (cents) => `${(cents / 100).toLocaleString("fr-CH", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} CHF`;
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

const BAR_COLORS = ["#f59e0b", "#d97706", "#b45309", "#92400e", "#78716c", "#57534e"];

const ChartCard = ({ title, icon: Icon, right, children, testid }) => (
  <div data-testid={testid} className="bg-[#0F1115] border border-[#1e2129] rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-stone-200">
        <Icon className="w-4 h-4 text-amber-500" />
        <span className="font-serif text-lg">{title}</span>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const StatPill = ({ label, value }) => (
  <div className="text-center">
    <div className="font-serif text-2xl text-amber-300 leading-none">{value}</div>
    <div className="mt-1 font-mono text-[9px] uppercase tracking-wider text-stone-500">{label}</div>
  </div>
);

const tooltipStyle = {
  backgroundColor: "#15181f", border: "1px solid #2a2e38", borderRadius: 8,
  fontSize: 12, color: "#e7e5e4",
};
const axisProps = { stroke: "#57534e", tick: { fill: "#78716c", fontSize: 10 }, tickLine: false, axisLine: false };

export const AdminAnalytics = () => {
  const [period, setPeriod] = useState("30d");
  const { data, isLoading } = useQuery({
    queryKey: ["adminAnalytics", period],
    queryFn: async () => (await api.get(`/admin/analytics?period=${period}`)).data,
    refetchInterval: 120000,
  });

  const growth = data?.growth || { series: [], totals: {} };
  const revenue = data?.revenue || { series: [], by_kind: [] };
  const content = data?.content || { by_category: [], by_canton: [], top_cities: [], upcoming: [] };
  const community = data?.community || { posts_series: [], top_categories: [] };
  const store = data?.store || { top_products: [] };
  const directory = data?.directory || { top_viewed: [] };

  const kindLabels = {
    order: "Boutique", opportunity: "Appels à participation", pro_sub: "Abo. Pro", subscription: "Abonnements",
    promotion: "Mises en avant", donation: "Dons", directory_sub: "Annuaire", other: "Autre",
  };
  const revByKind = (revenue.by_kind || []).map((k) => ({ ...k, name: kindLabels[k.kind] || k.kind }));
  const cantonData = (content.by_canton || []).slice(0, 8).map((c) => ({ name: CANTON_NAMES[c.key] || c.key, count: c.count }));

  return (
    <div className="mb-14" data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-500" />
          <h2 className="font-serif text-2xl text-stone-100">Statistiques</h2>
        </div>
        <div className="inline-flex rounded-lg border border-[#272A34] bg-[#121418] p-1" data-testid="analytics-period-toggle">
          {PERIODS.map((p) => (
            <button key={p.key} data-testid={`analytics-period-${p.key}`} onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition-colors ${
                period === p.key ? "bg-amber-500/15 text-amber-300 border border-amber-600/40" : "text-stone-400 hover:text-stone-200"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-stone-500" data-testid="analytics-loading">Chargement des statistiques…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Growth */}
          <ChartCard title="Croissance" icon={TrendingUp} testid="chart-growth"
            right={<div className="flex gap-4"><StatPill label="Membres" value={growth.totals.members ?? 0} /><StatPill label="Abonnés" value={growth.totals.subscribers ?? 0} /></div>}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={growth.series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gMembers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gSubs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2129" vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={20} />
                <YAxis allowDecimals={false} {...axisProps} width={30} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#a8a29e" }} />
                <Area type="monotone" dataKey="members" name="Membres" stroke="#f59e0b" fill="url(#gMembers)" strokeWidth={2} />
                <Area type="monotone" dataKey="subscribers" name="Abonnés" stroke="#38bdf8" fill="url(#gSubs)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Events submitted vs approved */}
          <ChartCard title="Événements" icon={CalendarClock} testid="chart-events"
            right={<div className="flex gap-4"><StatPill label="Soumis" value={growth.totals.events_submitted ?? 0} /><StatPill label="Validés" value={growth.totals.events_approved ?? 0} /></div>}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={growth.series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2129" vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={20} />
                <YAxis allowDecimals={false} {...axisProps} width={30} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#a8a29e" }} />
                <Line type="monotone" dataKey="events_submitted" name="Soumis" stroke="#a8a29e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="events_approved" name="Validés" stroke="#34d399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Revenue */}
          <ChartCard title="Revenus (Stripe)" icon={Wallet} testid="chart-revenue"
            right={<div className="flex gap-4"><StatPill label="Période" value={chf(revenue.period_cents ?? 0)} /><StatPill label="Total" value={chf(revenue.all_time_cents ?? 0)} /></div>}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={revenue.series} margin={{ top: 5, right: 8, left: -6, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2129" vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={20} />
                <YAxis {...axisProps} width={44} tickFormatter={(v) => `${Math.round(v / 100)}`} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#a8a29e" }}
                  formatter={(v) => [chf(v), "Revenu"]} />
                <Area type="monotone" dataKey="amount_cents" name="Revenu" stroke="#f59e0b" fill="url(#gRev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            {revByKind.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="revenue-by-kind">
                {revByKind.map((k, i) => (
                  <div key={k.kind} className="flex items-center gap-1.5 text-xs text-stone-400">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                    <span className="text-stone-300">{k.name}</span>
                    <span className="text-stone-500">· {chf(k.amount_cents)}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>

          {/* Content coverage — cantons */}
          <ChartCard title="Couverture par canton" icon={MapPinned} testid="chart-cantons">
            {cantonData.length === 0 ? (
              <div className="py-16 text-center text-stone-500 text-sm">Aucun événement validé.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={cantonData} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2129" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} {...axisProps} />
                  <YAxis type="category" dataKey="name" {...axisProps} width={78} />
                  <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#a8a29e" }} cursor={{ fill: "#ffffff08" }} />
                  <Bar dataKey="count" name="Événements" radius={[0, 4, 4, 0]}>
                    {cantonData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Content — categories & cities */}
          <ChartCard title="Répartition éditoriale" icon={BarChart3} testid="chart-content">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-2">Par catégorie</div>
                {(content.by_category || []).length === 0 ? (
                  <div className="text-sm text-stone-500 py-4">—</div>
                ) : content.by_category.map((c, i) => (
                  <div key={c.key} className="flex items-center gap-2 py-1.5" data-testid={`cat-${c.key}`}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                    <span className="text-sm text-stone-300 flex-1 truncate">{c.label}</span>
                    <span className="font-mono text-sm text-amber-300">{c.count}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-2">Top villes</div>
                {(content.top_cities || []).length === 0 ? (
                  <div className="text-sm text-stone-500 py-4">—</div>
                ) : content.top_cities.map((c) => (
                  <div key={c.city} className="flex items-center gap-2 py-1.5">
                    <span className="text-sm text-stone-300 flex-1 truncate">{c.city}</span>
                    <span className="font-mono text-sm text-stone-400">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
            {(content.upcoming || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#1e2129]">
                <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-2">Prochains à l'affiche</div>
                {content.upcoming.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-sm">
                    <CalendarClock className="w-3.5 h-3.5 text-stone-600 shrink-0" />
                    <span className="text-stone-300 truncate flex-1">{e.title}</span>
                    <span className="text-xs text-stone-500 shrink-0">{[e.city, fmtDate(e.start_date)].filter(Boolean).join(" · ")}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>

          {/* Community engagement */}
          <ChartCard title="Engagement communauté" icon={MessagesSquare} testid="chart-community"
            right={<div className="flex gap-4"><StatPill label="Membres actifs" value={community.active_members ?? 0} /><StatPill label="Messages" value={community.messages_total ?? 0} /></div>}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={community.posts_series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2129" vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={20} />
                <YAxis allowDecimals={false} {...axisProps} width={30} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#a8a29e" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="count" name="Publications" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 flex items-center gap-5 text-xs text-stone-400">
              <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-stone-500" /> Conversations acceptées : <b className="text-stone-200">{community.threads_accepted ?? 0}</b></span>
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-stone-500" /> En attente : <b className="text-stone-200">{community.threads_pending ?? 0}</b></span>
            </div>
            {(community.top_categories || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                {community.top_categories.map((c) => (
                  <span key={c.key} className="text-xs text-stone-400">{c.key} <span className="text-amber-400/80">·{c.count}</span></span>
                ))}
              </div>
            )}
          </ChartCard>

          {/* Store — top selling products */}
          <ChartCard title="Produits les plus vendus" icon={Package} testid="chart-top-products">
            {(store.top_products || []).length === 0 ? (
              <div className="py-16 text-center text-stone-500 text-sm">Aucune vente enregistrée.</div>
            ) : (
              <div className="divide-y divide-[#1a1d24]">
                {store.top_products.map((p, i) => (
                  <div key={p.slug || i} data-testid={`top-product-${p.slug}`} className="flex items-center gap-3 py-2.5">
                    <span className="font-serif text-sm text-stone-600 w-5 shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-stone-200 truncate">{p.name}</div>
                      <div className="text-xs text-stone-500">{chf(p.revenue_cents)}</div>
                    </div>
                    <span className="font-mono text-sm text-amber-300 shrink-0">{p.units} vendu{p.units > 1 ? "s" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>

          {/* Directory — most viewed listings */}
          <ChartCard title="Annuaire — fiches les plus consultées" icon={Store} testid="chart-top-directory">
            {(directory.top_viewed || []).length === 0 ? (
              <div className="py-16 text-center text-stone-500 text-sm">Aucune consultation enregistrée pour l'instant.</div>
            ) : (
              <div className="divide-y divide-[#1a1d24]">
                {directory.top_viewed.map((d, i) => (
                  <div key={d.slug || i} data-testid={`top-directory-${d.slug}`} className="flex items-center gap-3 py-2.5">
                    <span className="font-serif text-sm text-stone-600 w-5 shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-stone-200 truncate">{d.name}</div>
                      {d.type ? <div className="text-xs text-stone-500 truncate">{d.type}</div> : null}
                    </div>
                    <span className="font-mono text-sm text-amber-300 shrink-0 inline-flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" />{d.views}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
